<template>
  <div class="fta-root">

    <!-- ── Skeleton iniziale ────────────────────────────────── -->
    <template v-if="isBooting">
      <div v-for="n in 3" :key="'ftsk_'+n" class="fta-card">
        <div class="fta-card-header">
          <div class="fta-skel fta-skel--title"></div>
        </div>
        <div v-for="r in 5" :key="'rowsk_'+r" class="fta-event-row fta-event-row--skel">
          <div class="fta-skel fta-skel--info"></div>
          <div v-for="o in 3" :key="'oddsk_'+o" class="fta-skel fta-skel--odd"></div>
        </div>
      </div>
    </template>

    <!-- ── Cards torneo ─────────────────────────────────────── -->
    <template v-else>
      <div
        v-for="object in featuredPreview"
        :key="'fta_card_'+object.tournament.id"
        :id="'fta-tournament-'+object.tournament.id"
        class="fta-card"
      >

        <!-- Header -->
        <div class="fta-card-header">
          <div class="fta-breadcrumbs">
            <img
              :src="`${tournamentPath}${object.tournament.id}.webp`"
              class="fta-thumb"
              @error="e => e.target.style.display='none'"
            />
            <span class="fta-crumb fta-crumb--bright">{{ object.tournament.name === "NCAA | American Football | Men" ? 'NCAA' : object.tournament.name }}</span>
          </div>
          <button class="fta-view-all-btn" @click.stop="viewAll(object)">
            {{ $t('view_all') || 'View All' }}
          </button>
        </div>

        <!-- Skeleton mentre carica il torneo -->
        <template v-if="isLoadingTournament(object)">
          <div class="fta-events-skeleton">
            <div v-for="r in 5" :key="'esk_'+r" class="fta-event-row fta-event-row--skel">
              <div class="fta-skel fta-skel--info"></div>
              <div v-for="o in (isSmallScreen ? 1 : 3)" :key="'osk_'+o" class="fta-skel fta-skel--odd"></div>
              <div class="fta-skel fta-skel--more"></div>
            </div>
          </div>
        </template>

        <!-- Eventi -->
        <template v-else-if="hasEvents(object)">
          <div class="fta-grouped-tournament" v-for="(group, date) in object.events" :key="'dg_'+date">

            <!-- Date + market header row -->
            <div class="fta-date-header">
              <div class="fta-date-cell">{{ reformatDate(group.scheduled) }}</div>
              <div class="fta-header-odds-area">
                <!-- 1 market: mostra i nomi degli outcome -->
                <template v-if="isSingleMarketMode(object)">
                  <div
                    v-for="(name, i) in getOutcomeHeadersForGroup(object, group)"
                    :key="'oh_'+i"
                    class="fta-market-name-cell"
                  >
                    <span>
                      {{ name }}
                    </span>
                  </div>
                </template>
                <!-- N market: mostra i nomi dei mercati (sempre 3 colonne) -->
                <template v-else>
                  <div
                    v-for="(mDef, i) in paddedMarkets(object)"
                    :key="'mh_'+i"
                    class="fta-market-name-cell"
                  >
                    <span>
                      {{ mDef ? (mDef.marketAlias || mDef.marketName) : '' }}
                    </span>
                    </div>
                </template>
              </div>
            </div>

            <!-- Event rows -->
            <div
              v-for="(event, eIdx) in group.events"
              :key="'ev_'+event.id"
              class="fta-event-row"
              :class="eIdx % 2 === 0 ? 'fta-row--even' : 'fta-row--odd'"
            >
              <!-- Metà sinistra: time + competitors -->
              <div class="fta-info-cell" @click="goToDetail(event, object)">
                <div class="fta-event-time">{{ formatTime(event.scheduled) }}</div>
                <div class="fta-competitors">
                  <div class="fta-team">
                    <LogoCompetitors class="fta-team-logo" :team-id="event.home_id" />
                    <span class="fta-team-name">{{ event.home }}</span>
                  </div>
                  <div class="fta-team">
                    <LogoCompetitors class="fta-team-logo" :team-id="event.away_id" />
                    <span class="fta-team-name">{{ event.away }}</span>
                  </div>
                </div>
              </div>

              <!-- Metà destra: sempre 3 colonne -->
              <div class="fta-odds-area">

                <!-- 1 market: ogni outcome occupa una colonna -->
                <template v-if="isSingleMarketMode(object)">
                  <div
                    v-for="(odd, oIdx) in getSingleMarketOutcomes(event, visibleMarkets(object)[0])"
                    :key="'so_'+event.id+'_'+oIdx"
                    class="fta-outcome-btn"
                    :class="[
                      odd && isValidOdd(odd) ? 'fta-outcome-btn--active' : 'fta-outcome-btn--locked',
                      odd && getOddsSources.includes(odd.unique_id) ? 'fta-outcome-btn--selected' : '',
                      odd && odd.type_change || ''
                    ]"
                    @click.stop="putCart(getEventMarket(event, visibleMarkets(object)[0]), odd, event, object)"
                  >
                    <template v-if="odd && isValidOdd(odd)">
                      <span class="fta-outcome-label">
                        <span
                          v-if="odd.line != null && String(odd.line) !== ''"
                          class="fta-line"
                          :class="getSpecifierType(odd.line) + '-text'"
                        >{{ formatLineDisplay(odd.line, visibleMarkets(object)[0]) }}</span>
                      </span>
                      <span class="fta-outcome-name">{{ getOutcomeName(event.competitors, odd, getEventMarket(event, visibleMarkets(object)[0]).unique_id) }}</span>
                      <span class="fta-outcome-val">{{ odd.outcome_odd_value | odds_type_filter | formatDecimal }}<OddBoostBadge :odd="odd" size="sm" :show-percent="false"/></span>
                    </template>
                    <v-icon v-else dense small>mdi-lock</v-icon>
                  </div>
                </template>

                <!-- N market: ogni market in una colonna, quote impilate -->
                <template v-else>
                  <div
                    v-for="(marketDef, mIdx) in paddedMarkets(object)"
                    :key="'mc_'+event.id+'_'+mIdx"
                    class="fta-market-col"
                  >
                    <template v-if="marketDef && getEventMarket(event, marketDef) && getEventMarket(event, marketDef).outcome">
                      <div
                        v-for="odd in getEventMarket(event, marketDef).outcome"
                        :key="odd.unique_id"
                        class="fta-outcome-btn"
                        :class="[
                          isValidOdd(odd) ? 'fta-outcome-btn--active' : 'fta-outcome-btn--locked',
                          getOddsSources.includes(odd.unique_id) ? 'fta-outcome-btn--selected' : '',
                          odd.type_change || ''
                        ]"
                        @click.stop="putCart(getEventMarket(event, marketDef), odd, event, object)"
                      >
                        <template v-if="isValidOdd(odd)">

                          <span class="fta-outcome-name">{{ getOutcomeName(event.competitors, odd, getEventMarket(event, marketDef).unique_id) }} {{ odd.line != null && String(odd.line) !== '' ?  formatLineDisplay(odd.line, marketDef) : ''}}</span>
                          <span class="fta-outcome-val">{{ odd.outcome_odd_value | odds_type_filter | formatDecimal }}<OddBoostBadge :odd="odd" size="sm" :show-percent="false"/></span>
                        </template>
                        <v-icon v-else dense small>mdi-lock</v-icon>
                      </div>
                    </template>
                    <template v-else>
                      <div class="fta-outcome-btn fta-outcome-btn--locked">
                        <v-icon dense small>mdi-lock</v-icon>
                      </div>
                    </template>
                  </div>
                </template>

              </div>
            </div>

          </div>

          <!-- Load more -->
<!--          <div-->
<!--            v-if="getPaginationBySource(object.source) && getPaginationBySource(object.source).page < getPaginationBySource(object.source).totalPages"-->
<!--            class="fta-load-more"-->
<!--          >-->
<!--            <button class="fta-load-more-btn" :disabled="isLoadingMore" @click="loadMore(object)">-->
<!--              <v-progress-circular v-if="isLoadingMore" indeterminate size="18" width="2" color="var(&#45;&#45;color-primary)" />-->
<!--              <span v-else>-->
<!--                {{ $t('load_more') || 'Carica altri' }}-->
<!--                ({{ getPaginationBySource(object.source).total - getPaginationBySource(object.source).page * getPaginationBySource(object.source).limit }})-->
<!--              </span>-->
<!--            </button>-->
<!--          </div>-->
        </template>

        <!-- Empty state -->
        <div v-else class="fta-empty">
          <v-icon size="36" color="#555">mdi-calendar-remove</v-icon>
          <span>{{ $t('noEventsAvailableForThisMarket') || 'No events available' }}</span>
        </div>

      </div>
    </template>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { isMobileOnly, isTablet } from 'mobile-device-detect';
import { resolveEventMarket } from '@/utils/marketLineUtils';
import LogoCompetitors from '@/components/center_widgets/LogoCompetitors.vue';
import OddBoostBadge from '@/components/generic/OddBoostBadge.vue';
import {MARKET_ASIAN_HANDICAP, MARKET_NOT_NAME_IN_OUTCOME} from '../../constants/marketConstants';
export default {
  name: 'FeaturedTournamentsAmerican',

  components: { LogoCompetitors, OddBoostBadge },

  filters: {
    formatDecimal(value) {
      if (value === undefined || value === null) return '';
      const str = String(value).trim();
      if (str.includes('/') || str.startsWith('+') || str.startsWith('-')) return str;
      const num = parseFloat(str);
      return isNaN(num) ? str : num.toFixed(2);
    },
  },

  data: () => ({
    isSmallScreen: false,
    tournamentPath: process.env.VUE_APP_URL_CLOUDFRONT + '/svg/tournaments/',
  }),

  computed: {
    ...mapGetters({
      getFeaturedPreview:           'previewPrematch/getFeaturedPreview',
      getLoadingPreviewIds:         'previewPrematch/getLoadingPreview',
      isLoadingMore:                'previewPrematch/isLoadingMore',
      getPaginationBySource:        'previewPrematch/getPaginationBySource',
      getOddsSources:               'cart/getOddsSources',
      getLanguage:                  'settings/getLanguage',
      getMarketConfig:              'settings/getMarketConfig',
      getOpenSportId:               'topsports/getOpenSportId',
      getFeaturedTournaments:       'featuredEvents/getFeaturedTournaments',
      getFeaturedTournamentsLoading: 'featuredEvents/getFeaturedTournamentsLoading',
      getCompetitorsInOutcome:      'settings/getCompetitorsInOutcome',
    }),

    isBooting() {
      return this.getFeaturedTournamentsLoading && this.getFeaturedTournaments.length === 0;
    },

    featuredSources() {
      return this.getFeaturedTournaments.map(t => t.tournamentId + '#prematch');
    },

    // Solo i tornei del sport selezionato
    featuredPreview() {
      return this.getFeaturedPreview.filter(p =>
        this.featuredSources.includes(p.source) &&
        (!this.getOpenSportId || String(p.sport.id) === String(this.getOpenSportId)),
      );
    },
  },

  methods: {
    ...mapActions({
      fetchPreviewV3:         'previewPrematch/fetchPreviewV3',
      fetchEventDetailV3:     'detailPrematch/fetchEventDetailV3',
      setLoadingPreview:      'odds/setLoadingPreview',
      addOddToCartV3:         'cart/addOddToCartV3',
      setSubaction:           'ui/setSubaction',
      setStatusQbet:          'tree/setStatusQbet',
      setViewMode:            'ui/setViewMode',
      closeTopsportsPanel:    'topsports/closePanel',
      clearAllDetail:         'detailPrematch/clearAllDetail',
      removeFromPreview:      'previewPrematch/removeFromPreview',
    }),

    // ── Helpers store ─────────────────────────────────────────

    isLoadingTournament(object) {
      return this.getLoadingPreviewIds.includes(object.tournament.id);
    },

    hasEvents(object) {
      return object.events && Object.keys(object.events).length > 0;
    },

    visibleMarkets(object) {
      return (object.headerSign || []).slice(0, 3);
    },

    async loadMore(object) {
      if (this.isLoadingMore) return;
      const pagination = this.getPaginationBySource(object.source);
      if (!pagination) return;
      await this.fetchFeaturedPreviewV3({
        source: object.source, sportId: object.sport.id, categoryId: object.category.id,
        tournament: object.tournament.id, type: object.type,
        page: pagination.page + 1, limit: pagination.limit,
      });
    },

    // ── Odds / mercati ────────────────────────────────────────

    getEventMarket(event, headerMarketDef) {
      if (!event.odds) return null;
      const mId = String(headerMarketDef.marketId);
      const matches = event.odds.filter(m => String(m.id) === mId);
      return resolveEventMarket(matches, null);
    },

    isValidOdd(odd) {
      return odd && parseFloat(odd.outcome_odd_value) > 1 && odd.active;
    },

    getSpecifierType(specifier) {
      if (specifier == null || String(specifier) === '') return 'neutral';
      const str = String(specifier).trim();
      if (str.startsWith('-')) return 'negative';
      if (str.startsWith('+')) return 'positive';
      return 'neutral';
    },

    stripScoreState(str) {
      return str ? String(str).replace(/\s*\(\d+-\d+\)\s*$/, '').trim() : str;
    },

    formatLineDisplay(line, marketDef) {
      if (line == null || String(line) === '') return '';
      let str = this.stripScoreState(String(line).trim());
      const mId = parseInt(marketDef.id || marketDef.marketId, 10);
      if (this.getMarketConfig.handicapIds.includes(mId)) {
        if (/^[0-9]/.test(str) && parseFloat(str) !== 0) return '+' + str;
      }

      if (MARKET_ASIAN_HANDICAP.includes(marketDef.marketId) && !str.startsWith('-'))
        return '+' + str

      return str;
    },

    getOutcomeName(competitors, odd, uniqueId) {

      if (MARKET_NOT_NAME_IN_OUTCOME.includes(uniqueId)) {
        return odd.outcome_alias || odd.outcome_name;
      }

      if(odd.name === 'Under') return "U"
      else if(odd.name === 'Over') return "O"
      else return ""
      const outcome = odd.outcome_name?.toLowerCase();
      if (odd.outcome_name === 'X') return odd.outcome_alias || odd.outcome_name;
      if (outcome === 'local') return competitors?.[0]?.name || odd.outcome_name;
      if (outcome === 'visitante') return competitors?.[1]?.name || odd.outcome_name;
      if ((odd.outcome_name === '1' || odd.outcome_name === '2') && uniqueId != '128') {
        for (const c of (competitors || [])) {
          if (c.position === odd.outcome_name) return c.name;
        }
      }
      return odd.outcome_alias || odd.outcome_name;
    },

    getTranslatedMarketName(market) {
      const d = market.detail ? market.detail.find(x => x.lang === this.getLanguage) : null;
      return d ? d.text : market.marketAlias || market.name;
    },

    // ── Layout helpers ────────────────────────────────────────

    isSingleMarketMode(object) {
      return this.visibleMarkets(object).length === 1;
    },

    paddedMarkets(object) {
      const markets = this.visibleMarkets(object).slice(0, 3);
      while (markets.length < 3) markets.push(null);
      return markets;
    },

    getOutcomeHeaders(object) {
      const mDef = this.visibleMarkets(object)[0];
      if (!mDef) return ['', '', ''];
      if (mDef.outcomeNames && mDef.outcomeNames.length >= 3) return mDef.outcomeNames.slice(0, 3);
      return [mDef.marketAlias || mDef.marketName, '', ''];
    },

    getOutcomeHeadersForGroup(object, group) {
      const firstEvent = group && group.events && group.events[0];
      if (!firstEvent) return this.getOutcomeHeaders(object);
      const mDef = this.visibleMarkets(object)[0];
      if (!mDef) return ['', '', ''];
      const market = this.getEventMarket(firstEvent, mDef);
      if (!market || !market.outcome || !market.outcome.length) return this.getOutcomeHeaders(object);
      const competitors = firstEvent.competitors || [];
      const outcomes = market.outcome.slice(0, 3);
      while (outcomes.length < 3) outcomes.push(null);
      return outcomes.map(odd => {
        if (!odd) return '';
        const name = odd.outcome_name;
        if (name === 'X') return odd.outcome_alias || odd.outcome_name;
        return odd.outcome_alias || odd.outcome_name;
      });
    },

    getSingleMarketOutcomes(event, marketDef) {
      if (!marketDef) return [null, null, null];
      const market = this.getEventMarket(event, marketDef);
      if (!market || !market.outcome) return [null, null, null];
      const outcomes = market.outcome.slice(0, 3);
      while (outcomes.length < 3) outcomes.push(null);
      return outcomes;
    },

    async viewAll(object) {
      this.closeTopsportsPanel();
      this.clearAllDetail();
      this.setStatusQbet(false);
      await this.setLoadingPreview({ status: true });

      // Rimuovi i preview esistenti così il torneo selezionato è l'unico mostrato
      for (const p of (this.getFeaturedPreview || [])) {
        if (p.source && p.source !== object.source) {
          await this.removeFromPreview({ source: p.source });
        }
      }

      if (this.isMobile) {
        this.$root.$emit('close_sidebar_left', true);
      }

      await this.fetchPreviewV3({
        source:               object.source,
        tournament:           object.tournament.id,
        sportId:              object.sport.id,
        categoryId:           object.category.id,
        type:                 'previewOdds',
        mobilePlatform:       isMobileOnly || isTablet,
        groupOddsWithSpreads: true,
        forceCallApi:         true,
      });

      if (this.isMobile) {
        this.setViewMode({ mode: 'preview' });
      }
      await this.setLoadingPreview({ status: false });
      this.$nextTick(() => window.scrollTo({ top: 0 }));

    },

    // ── Cart / navigazione ────────────────────────────────────

    async putCart(oddMarket, odd, event, object) {
      if (!this.isValidOdd(odd)) return;
      odd.type = 'prematch';
      odd.provider = event.provider || 'lsport';
      if (navigator.vibrate) navigator.vibrate(20);
      await this.addOddToCartV3({ oddMarket, odd, event, sport: object.sport, category: object.category, tournament: object.tournament });
      this.$root.$emit('forceUpdateSingleMultiple');
    },

    goToDetail(event, object) {
      if (window.innerWidth < 1200 || window.forceMobileView === 'true') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      this.setLoadingPreview({ status: true });
      this.fetchEventDetailV3({
        source: event.id, group: null,
        sport: object.sport.id, category: object.category.id, tournament: object.tournament.id,
        status: event.sport_event_status?._attributes?.status,
        startEventTime: event.scheduled,
        competitorHome: event.home, competitorAway: event.away,
        sportName: event.sportName, categoryName: event.categoryName, tournamentName: event.tournamentName,
        competitorHomeId: event.home_id, competitorAwayId: event.away_id,
      }).then(() => this.setLoadingPreview({ status: false }));
    },

    // ── Date / time ───────────────────────────────────────────

    reformatDate(date) {
      if (!date) return '';
      const d = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(date + 'T12:00:00') : new Date(date);
      return d.toLocaleDateString([], { weekday: 'short', day: '2-digit', month: '2-digit' }).replace(/,/g, '');
    },

    formatTime(date) {
      return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    // ── Resize / drag ─────────────────────────────────────────

    onResize() {
      this.isSmallScreen = window.innerWidth <= 600;
    },

  },

  mounted() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
};
</script>

<style scoped>
/* ── Root ───────────────────────────────────────────────────── */
.fta-root {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0;
}

/* ── Card torneo ────────────────────────────────────────────── */
.fta-card {
  border-radius: 6px;
  overflow: hidden;
  scroll-margin-top: 46px;
}

.fta-grouped-tournament{
  margin-top: 12px;
  border-radius: .375rem;
  overflow: hidden;

}

/* ── Header ─────────────────────────────────────────────────── */
.fta-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 0 0 ;
  border-radius: inherit;
}

.fta-breadcrumbs {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-7xl);
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  text-transform: uppercase;
}

.fta-thumb {
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 3px;
  flex-shrink: 0;
}

.fta-crumb {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fta-crumb--dim  { color: var(--text-platform); font-weight: 500; }
.fta-crumb--bright { color: var(--text-platform); font-weight: 700; }
.fta-sep { color: var(--text-platform); font-size: var(--font-size-md); flex-shrink: 0; }

/* ── View All button ────────────────────────────────────────── */
.fta-view-all-btn {
  height: 25px;
  color: #fff;
  border-radius: 15px;
  padding: 0 10px;
  font-size: var(--font-size-2xl);
  font-weight: 600;
  border: 1px solid hsla(0, 0%, 100%, 0.5);
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
  white-space: nowrap;
}
.fta-view-all-btn:hover {
  background: hsla(0, 0%, 100%, 0.1);
}

/* ── Date header row ────────────────────────────────────────── */
.fta-date-header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  padding: 4px 0;
  background: var(--bg-card);
}

.fta-header-odds-area {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
}

.fta-date-cell {
  font-size: var(--font-size-xl);
  font-weight: 700;
  color: var(--text-platform);
  text-transform: uppercase;
  display: flex;
  align-items: center;
  padding: 0 12px;
}

.fta-market-name-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--text-platform);
  text-align: center;
  text-transform: uppercase;
  padding: 0 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fta-market-name-cell span {

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Event row ──────────────────────────────────────────────── */
.fta-event-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: stretch;
  min-height: 52px;
  border-bottom: 1px solid var(--hsla-1);
}

.fta-row--even { background: var(--event-background); }
.fta-row--odd  { background: var(--event-background); }

/* Info cell */
.fta-info-cell {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px 8px 8px 12px;
  cursor: pointer;
  overflow: hidden;
}

@media (max-width: 600px) {
  .fta-info-cell{
    flex-direction: column-reverse;
    align-items: flex-start;
    justify-content: center;
    padding: 0px 8px 0px 12px;

  }
}

.fta-event-time {
  flex-shrink: 0;
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--text-op-50-platform);
  line-height: 1;
  white-space: nowrap;
}

.fta-competitors {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fta-team {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
}

.fta-team-logo {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.fta-team-name {
  font-size: var(--font-size-3xl);
  font-weight: 600;
  color: var(--text-platform);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Odds area: always 3 columns */
.fta-odds-area {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: stretch;
}

/* Market column (multi-market mode): outcomes stacked */
.fta-market-col {
  display: flex;
  flex-direction: column;
  gap: 0px;
  align-items: stretch;
}

/* Outcome button */
.fta-outcome-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  min-height: 44px;
  padding: 4px 6px;
  text-align: center;
  transition: background 0.12s, transform 0.1s;
  border-left: 1px solid var(--hsla-1);
  background: var(--bg-cell);

}

.fta-outcome-btn--active {
  cursor: pointer;
}

.fta-outcome-btn--active:hover {
  background: hsla(0,0%,100%,.14901960784313725);
}

.fta-outcome-btn--locked {
  background: transparent;
  color: var(--text-op-50-platform);
}

.fta-outcome-btn--selected {
  background: var(--bg-cell-active) !important;
  color: var(--text-cell-active) !important;
}
.fta-outcome-btn--selected .fta-outcome-name,
.fta-outcome-btn--selected .fta-outcome-val {
  color: var(--text-cell-active) !important;
}


.fta-outcome-label {
  font-size: var(--font-size-2xl);
  color: var(--text-op-50-platform);
  line-height: 1;
}

.fta-outcome-name {
  font-size: var(--font-size-2xl);
  color: var(--text-op-50-platform);
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.fta-outcome-val {
  font-size: var(--font-size-3xl);
  font-weight: 700;
  color: var(--text-bg-cell);
  line-height: 1;
}

.fta-line {
  font-size: var(--font-size-md);
  font-weight: 600;
}
.neutral-text { color: var(--text-op-50-platform); }
.positive-text { color: #4caf50; }
.negative-text { color: #ef5350; }

/* Up/down animation */
.up   { animation: flash-up   0.6s ease; }
.down { animation: flash-down 0.6s ease; }
@keyframes flash-up   { 0%,100%{ background: inherit; } 50%{ background: rgba(76,175,80,0.25); } }
@keyframes flash-down { 0%,100%{ background: inherit; } 50%{ background: rgba(239,83,80,0.25); } }

/* ── Load more ──────────────────────────────────────────────── */
.fta-load-more {
  display: flex;
  justify-content: center;
  padding: 12px;
}

.fta-load-more-btn {
  padding: 8px 24px;
  border-radius: 6px;
  background: var(--btn-background);
  color: var(--btn-text, #fff);
  font-size: var(--font-size-2xl);
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}
.fta-load-more-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Empty state ────────────────────────────────────────────── */
.fta-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 16px;
  color: var(--text-op-50-platform);
  font-size: var(--font-size-3xl);
}

/* ── Skeleton ───────────────────────────────────────────────── */
@keyframes fta-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.fta-skel {
  border-radius: 4px;
  background: linear-gradient(90deg, #252b34 30%, #374256 50%, #252b34 70%);
  background-size: 200% 100%;
  animation: fta-shimmer 1.6s infinite linear;
}

.fta-skel--title { height: 14px; width: 55%; }
.fta-skel--info  { height: 40px; flex: 1; }
.fta-skel--odd   { height: 40px; width: 70px; border-radius: 4px; flex-shrink: 0; }
.fta-skel--more  { height: 36px; width: 52px; border-radius: 4px; flex-shrink: 0; }

.fta-events-skeleton {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.fta-skel-header-row {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 6px 12px;
  background: var(--bg-card);
}

.fta-event-row--skel {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--event-background);
}

@media (max-width: 600px) {
  .fta-event-row{
    padding: 4px 0;
  }
}
</style>
